import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Check, ChevronLeft } from "lucide-react";
import Seo from "../components/site/Seo";
import DynamicForm from "../components/forms/DynamicForm";
import { AvailabilityBadge } from "../components/site/ProductCard";
import { Disclaimer } from "../components/site/Bits";
import { api, mediaUrl } from "../lib/api";

export default function ProductDetail() {
  const { slug } = useParams();
  const [img, setImg] = useState(0);
  const { data: p, isLoading, isError } = useQuery({ queryKey: ["product", slug], queryFn: () => api.get(`/public/products/${slug}`).then((r) => r.data) });

  if (isLoading) return <p className="container-x py-20 text-lg" role="status">Loading equipment…</p>;
  if (isError || !p) return (
    <div className="container-x py-20"><h1 className="font-display text-3xl font-semibold">Equipment not found</h1><Link to="/equipment" className="btn-primary mt-6">Back to catalogue</Link></div>
  );

  return (
    <>
      <Seo title={`${p.name} – Trial at Home`} path={`/equipment/${p.slug}`} description={`${p.summary} Request an NDIS equipment trial at home in Melbourne.`} image={p.images?.[0]} />
      <div className="container-x py-10">
        <Link to="/equipment" className="inline-flex min-h-[48px] items-center gap-1 font-semibold text-teal hover:underline"><ChevronLeft size={20} aria-hidden="true" />Back to catalogue</Link>
        <div className="mt-6 grid gap-10 lg:grid-cols-2">
          <div>
            <div className="overflow-hidden rounded-[2rem] bg-sand">
              {p.images?.[img] && <img src={mediaUrl(p.images[img])} alt={p.image_alts?.[img] || p.name} className="aspect-[4/3] w-full object-cover" />}
            </div>
            {p.images?.length > 1 && (
              <ul className="mt-4 flex gap-3" aria-label="More images">
                {p.images.map((u, i) => (
                  <li key={u}><button type="button" onClick={() => setImg(i)} aria-pressed={img === i} aria-label={`Show image ${i + 1}`}
                    className={`overflow-hidden rounded-xl border-2 ${img === i ? "border-teal" : "border-line"}`}><img src={mediaUrl(u)} alt="" className="h-20 w-24 object-cover" /></button></li>
                ))}
              </ul>
            )}
          </div>
          <div>
            <p className="eyebrow">{p.category}</p>
            <h1 className="mt-2 font-display text-4xl font-bold text-teal sm:text-5xl" data-testid="product-title">{p.name}</h1>
            <div className="mt-4 flex flex-wrap items-center gap-3"><AvailabilityBadge value={p.availability} />{p.is_sample && <span className="rounded-full bg-sand px-3 py-1 text-sm font-bold">Sample data</span>}</div>
            <p className="mt-6 text-lg">{p.description}</p>
            {p.features?.length > 0 && (
              <>
                <h2 className="mt-8 font-display text-2xl font-semibold">Key features</h2>
                <ul className="mt-4 space-y-2">{p.features.map((f) => <li key={f} className="flex gap-3"><Check className="mt-1 shrink-0 text-success" size={20} aria-hidden="true" />{f}</li>)}</ul>
              </>
            )}
            <h2 className="mt-8 font-display text-2xl font-semibold">Specifications</h2>
            <dl className="mt-4 divide-y divide-line rounded-2xl border border-line bg-surface">
              <div className="grid grid-cols-[10rem_1fr] gap-4 p-4"><dt className="font-semibold">Dimensions</dt><dd>{p.dimensions || "Ask us"}</dd></div>
              <div className="grid grid-cols-[10rem_1fr] gap-4 p-4"><dt className="font-semibold">Weight capacity</dt><dd>{p.weight_capacity || "Ask us"}</dd></div>
            </dl>
            <a href="#trial-form" className="btn-primary mt-8" data-testid="product-request-trial-btn">Request a Trial of this item</a>
          </div>
        </div>
        <section id="trial-form" className="mx-auto mt-20 max-w-4xl scroll-mt-28" aria-labelledby="trial-form-title">
          <h2 id="trial-form-title" className="font-display text-3xl font-semibold">Request a trial of {p.name.replace("[Sample] ", "")}</h2>
          <p className="mb-6 mt-3 text-lg text-ink-muted">We'll contact you to confirm your needs with your treating therapist before arranging delivery.</p>
          <DynamicForm kind="trial" testid="product-trial-form" initial={{ equipment: p.name, product_id: p.id }} />
          <div className="mt-8"><Disclaimer /></div>
        </section>
      </div>
    </>
  );
}
